import update from "immutability-helper";

import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootEntity } from "@types-runway/state";
import { Gender, User } from "@types-runway/user";

export const userObject: RootEntity<User> = {
  isPending: false,
  success: { type: null },
  data: {
    id: "",
    customer_id: "",
    first_name: "",
    last_name: "",
    email: "",
    phone_number: "",
    phone_type: 2,
    date_of_birth: new Date(),
    stateOfResidence: "",
    height: 0,
    weight: 0,
    active: true,
    gender_label: "",
    current_medications: "",
    medicalConditions: "",
    allergies: "",
    "patient-id": null,
    pregnancy: false,
    addresses: [],
    password: "",
    confirmPassword: "",
    driver_license_id: "",
    driver_license_case: "",
    id_requested: false,
    isAdmin: false,
    pharmacy_id: "",
    paymentMethods: [],
    defaultPaymentMethod: "",
    promocodes: [],
  },
  error: {},
};

export const userSlice = createSlice({
  name: "user",
  initialState: userObject,
  reducers: {
    userPending: (state) => {
      return Object.assign({}, state, {
        isPending: true,
        success: false,
      });
    },
    userError: (state, action) => {
      return Object.assign({}, state, {
        error: action.payload,
        isPending: false,
        success: false,
      });
    },
    userUpdate: (state, action) => {
      if (action.payload.name === "password") {
        return Object.assign({}, state, {
          data: {
            ...state.data,
            password: action.payload.value,
            confirmPassword: action.payload.value,
          },
        });
      }
      if (action.payload.name === "gender_label") {
        switch (action.payload.value) {
          case "Male":
            return Object.assign({}, state, {
              data: {
                ...state.data,
                gender: Gender.Male,
                gender_label: action.payload.value,
              },
            });
          case "Female":
            return Object.assign({}, state, {
              data: {
                ...state.data,
                gender: Gender.Female,
                gender_label: action.payload.value,
              },
            });
          case "Other":
            return Object.assign({}, state, {
              data: {
                ...state.data,
                gender: Gender.Other,
                gender_label: action.payload.value,
              },
            });
        }
      }

      return Object.assign({}, state, {
        data: {
          ...state.data,
          [action.payload.name]: action.payload.value,
        },
      });
    },
    // Atomically set the uploaded guardian ID together with the case it belongs
    // to, so the value and its ownership marker can never desync (ZD-3226).
    // Payload is typed so the string contract the `=== String(activeCaseId)`
    // isolation check depends on is enforced at every dispatch site.
    setGuardianId: (
      state,
      action: PayloadAction<{ driver_license_id: string; driver_license_case: string }>
    ) => {
      return Object.assign({}, state, {
        data: {
          ...state.data,
          driver_license_id: action.payload.driver_license_id,
          driver_license_case: action.payload.driver_license_case,
        },
      });
    },
    userMutationSuccess: (state, action) => {
      return update(state, {
        isPending: {
          $set: false,
        },
        data: {
          $set: action.payload,
        },
        success: { $set: { type: "write" } },
      });
    },
    userFetchSuccess: (state, action) => {
      return update(state, {
        isPending: {
          $set: false,
        },
        data: {
          $set: action.payload,
        },
        success: { $set: { type: "read" } },
      });
    },
    addressesFetchSuccess: (state, action) => {
      return update(state, {
        isPending: {
          $set: false,
        },
        data: {
          addresses: {
            $set: action.payload,
          },
        },
        success: { $set: { type: "read" } },
      });
    },
    paymentMethodsFetchSuccess: (state, action) => {
      return update(state, {
        isPending: {
          $set: false,
        },
        data: {
          paymentMethods: {
            $set: action.payload,
          },
        },
        success: { $set: { type: "read" } },
      });
    },
    promoCodesFetchSuccess: (state, action) => {
      return update(state, {
        isPending: {
          $set: false,
        },
        data: {
          promocodes: {
            $set: action.payload,
          },
        },
        success: { $set: { type: "read" } },
      });
    },
    clearUser: (state) => {
      return (state = userObject);
    },
    cleanUserError: (state) => {
      return Object.assign({}, state, { error: {} });
    },
    clearSuccess: (state) => {
      return update(state, {
        success: { $set: { type: null } },
      });
    },
  },
});

export const {
  userPending,
  userError,
  userMutationSuccess,
  userFetchSuccess,
  userUpdate,
  setGuardianId,
  addressesFetchSuccess,
  paymentMethodsFetchSuccess,
  promoCodesFetchSuccess,
  clearUser,
  cleanUserError,
  clearSuccess,
} = userSlice.actions;

export default userSlice.reducer;
